/**
 * FEAT-011 — Dexie-backed per-connector persisted state.
 *
 * Stores ONLY the fields that need to survive a tab refresh:
 *   - `assignedVehicleId` — which vehicle profile is bound to this connector
 *   - `currentSocPct`     — running SoC for DC sessions (AC vehicles ignore it)
 *
 * Volatile connector state (status, activeTransactionId, instantaneous power,
 * meter readings) stays in memory in `useStationStore` — it is reconstructed
 * from `transactions` + `meters` on hydrate.
 *
 * Compound PK `[stationId+connectorId]` matches storage-schema.yaml.
 */
import type { StationId } from '@/domain/types';
import type {
  ConnectorStateRepository,
  ConnectorStateRow,
} from '@/persistence/ports';
import type { Evse3Database } from '../db';

export class ConnectorStateRepositoryDexie implements ConnectorStateRepository {
  constructor(private readonly db: Evse3Database) {}

  async byStation(stationId: StationId): Promise<readonly ConnectorStateRow[]> {
    return this.db.connectorState.where('stationId').equals(stationId).toArray();
  }

  async get(
    stationId: StationId,
    connectorId: number,
  ): Promise<ConnectorStateRow | null> {
    const row = await this.db.connectorState.get([stationId, connectorId]);
    return row ?? null;
  }

  async upsert(row: ConnectorStateRow): Promise<void> {
    await this.db.connectorState.put({ ...row });
  }

  async delete(stationId: StationId, connectorId: number): Promise<void> {
    await this.db.connectorState.delete([stationId, connectorId]);
  }
}
